import React from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';

import { Columns, Column, MuiTitle } from 'src/ui/mui-ui-kit';;
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import {
  setBreadcrumbs,
  reduceServerErrors,
  ConfirmDeletionModal,
  UiToastAdapter,
} from 'src/ui';
import { getToken } from 'src/auth';
import { getUser, fetchLoggedInUser, impersonateAccount } from 'src/user';
import {
  getAccounts,
  fetchAccount,
  fetchAccounts,
  deleteAccount,
  fetchFormData,
} from 'src/account';
import { NavLink } from 'react-router-dom';
import { AccountsTable, SetStatusModal } from '../components';
import { updateAccountStatus } from '../redux';
import { ACCOUNT_STATUS } from '../const';

// import { getStatusChangeMessage } from '../services';
// import { updateAccount } from 'src/redux';

const AccountsListPage = ({
  fetchLoggedInUser,
  fetchAccounts,
  setBreadcrumbs,
  deleteAccount,
  token,
  user,
  accounts,
  impersonateAccount,
  fetchAccount,
  updateAccountStatus,
  fetchFormData,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [deletingAccount, setDeletingAccount] = React.useState(null);
  const [isStatusModalVisibile, setIsStatusModalVisibile] = React.useState(
    null
  );
  // the handlers below outlive the render they were created in
  const accountsRef = React.useRef(accounts);
  accountsRef.current = accounts;
  React.useEffect(() => {
    (async () => {
      setBreadcrumbs([{ label: 'Accounts', href: '/accounts' }]);
      if (token.isValid()) {
        await impersonateAccount({});
        await fetchLoggedInUser();
        if (user.financialInstitutionId) {
          await fetchAccounts({
            financialInstitutionId: user.financialInstitutionId,
            userId: user.id,
          });
          if (accountsRef.current.isError()) {
            reduceServerErrors(accountsRef.current);
          }
        }
      }
      setLoading(false);
    })();
  }, []);
  const onEditStatus = React.useCallback(
    account => {
      setIsStatusModalVisibile(account);
    },
    [isStatusModalVisibile]
  );
  const onEditAccount = React.useCallback(
    async account => {
      await impersonateAccount({
        accountId: account.id,
        accountName: account.businessName,
        hasConsents: account.hasConsents,
        hasDocuments: account.hasDocuments,
      });
      await fetchAccount(account.id);
      await fetchFormData(user.financialInstitutionId, account.id);
      setLoading(false);
    },
    [impersonateAccount, fetchAccount, fetchFormData]
  );

  const onDeleteAccount = React.useCallback(
    account => {
      setDeletingAccount(account);
    },
    [setDeletingAccount]
  );

  const onCancelDeleteAccount = React.useCallback(() => {
    setDeletingAccount(null);
  }, [setDeletingAccount]);

  const onConfirmDeleteAccount = React.useCallback(async () => {
    setLoading(true);
    onCancelDeleteAccount();

    await deleteAccount(user.financialInstitutionId, deletingAccount.id);

    setLoading(false);

    if (accountsRef.current.isError()) {
      return reduceServerErrors(accountsRef.current);
    }
    if (deletingAccount.id === user.accountId) {
      impersonateAccount({});
    }
    toast(UiToastAdapter('Account archived successfully!', 'success'));
    // this.pagedAccounts.refresh();
    return undefined;
  }, [setLoading, onCancelDeleteAccount, deleteAccount, deletingAccount]);

  const onChangeAccountStatus = async (account, status) => {
    setLoading(true);
    await updateAccountStatus({
      financialInstitutionId: user.financialInstitutionId,
      accountId: account.id,
      status,
    });

    if (accountsRef.current.isError()) {
      reduceServerErrors(accountsRef.current);
    } else {
      toast(UiToastAdapter('Account updated successfully!', 'success'));
      setIsStatusModalVisibile(null);
    }
    // } else {
    //   toast(
    //     UiToastAdapter(
    //       'There was an error updating the account. Try again or contact Solvent.',
    //       'danger'
    //     )
    //   );
    // }
    setLoading(false);
  };

  const onApproveAccount = React.useCallback(
    account => {
      return onChangeAccountStatus(account, ACCOUNT_STATUS.ACTIVE);
    },
    [onChangeAccountStatus]
  );

  const onDenyAccount = React.useCallback(
    account => {
      return onChangeAccountStatus(account, ACCOUNT_STATUS.NOT_ACTIVE);
    },
    [onChangeAccountStatus]
  );

  return (
    <>
      <Columns>
        <Column>
          <MuiTitle isSize="4" hasTextColor="info" tag="h2">
            Accounts
          </MuiTitle>
        </Column>
        <Column>
          {!user.isBankReadOnly && (
            <NavLink
              className="button is-gradient-bright is-long is-pulled-right"
              to="/accounts/add"
            >
              <span className="icon is-left">
                <FontAwesomeIcon icon={['fal', 'plus']} />
              </span>
              <span>Add Account</span>
            </NavLink>
          )}
        </Column>
      </Columns>
      <AccountsTable
        loading={loading}
        accounts={accounts.items || []}
        onEditAccount={onEditAccount}
        onDeleteAccount={onDeleteAccount}
        onApproveAccount={onApproveAccount}
        onDenyAccount={onDenyAccount}
        onEditStatus={onEditStatus}
        showEllipsis={!user.isBankReadOnly}
        hasAssignAgentsPage={user.financialInstitutionHasAssignAgents}
      />
      <ConfirmDeletionModal
        isActive={!!deletingAccount}
        onClickCancel={onCancelDeleteAccount}
        onClickConfirm={onConfirmDeleteAccount}
        title={deletingAccount ? deletingAccount.businessName : ''}
      />

      {isStatusModalVisibile && (
        <SetStatusModal
          isActive={isStatusModalVisibile}
          onSubmit={(account, status) => onChangeAccountStatus(account, status)}
          onCancel={() => setIsStatusModalVisibile(null)}
        />
      )}
    </>
  );
};

AccountsListPage.propTypes = {
  fetchLoggedInUser: propTypes.func.isRequired,
  fetchAccounts: propTypes.func.isRequired,
  setBreadcrumbs: propTypes.func.isRequired,
  deleteAccount: propTypes.func.isRequired,
  token: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
  accounts: propTypes.object.isRequired,
  impersonateAccount: propTypes.func.isRequired,
  fetchAccount: propTypes.func.isRequired,
  updateAccountStatus: propTypes.func.isRequired,
  fetchFormData: propTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    token: getToken(state),
    user: getUser(state),
    accounts: getAccounts(state),
  };
}

const connectedLocatioinsListPage = connect(mapStateToProps, {
  fetchLoggedInUser,
  fetchAccounts,
  fetchAccount,
  setBreadcrumbs,
  deleteAccount,
  impersonateAccount,
  updateAccountStatus,
  fetchFormData,
})(AccountsListPage);

export { connectedLocatioinsListPage as AccountsListPage };
